// import { GatsbyImage } from 'gatsby-plugin-image';
import React, { Dispatch, SetStateAction } from 'react';

import LocationPin from '@/assets/icons/location-pin.svg';

import { ClickHistory } from '../../floor-plan/floor-plan';
import {
  ActiveBuilding,
  FilterTile,
  MapTile,
} from '../../floor-plan-map/floor-plan-map.interface';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import { FloorPlanFilterListType } from '../floor-plan-filter-list-type.type';
import Image from 'next/image';
import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';

interface FloorPlanFilterTileProps {
  tile: FilterTile;
  updateFilterList?: React.Dispatch<
    React.SetStateAction<FloorPlanFilterListType>
  >;
  activeStore: MapTile | undefined;
  setActiveStore: Dispatch<SetStateAction<MapTile | undefined>>;
  activeBuilding: ActiveBuilding | undefined;
  filterValue: string;
  setClickHistory: Dispatch<SetStateAction<ClickHistory[]>>;
  clickHistory: ClickHistory[];
  clickOnTenant: (id: FilterTile) => Promise<void>;
  goToFloor: (id?: string) => Promise<void>;
  checkFloorOfID: (id: string) => string | false | undefined;
  getLocalizedShortName: (id: string) => string | false | undefined;
  filterByCategory?: (category: string, type?: FloorPlanFilterListType) => void;
  isLastTile: boolean;
  node_locale: string;
}

export const FloorPlanFilterTile = ({
  tile,
  updateFilterList,
  activeStore,
  setActiveStore,
  setClickHistory,
  clickHistory,
  clickOnTenant,
  goToFloor,
  checkFloorOfID,
  getLocalizedShortName,
  filterByCategory,
  isLastTile,
  node_locale,
  activeBuilding,
}: FloorPlanFilterTileProps) => {

  const activeStoreMatch =
    activeStore && (tile.floorPlanIDs?.includes(activeStore?.id) ||
      tile.unitId?.includes(activeStore?.id));

  const tileIsTenant =
    tile.type === 'MeetingPlacePageTenantShop' ||
    tile.type === 'MeetingPlacePageTenantFoodBeverage' ||
    tile.type === 'MeetingPlacePageTenantEntertainment' ||
    tile.type === 'MeetingPlacePageService';

  const handleOnClick = async () => {
    if (tile.type) {
      if (tile.shouldSkipCategories) {
        filterByCategory?.('all', tile.type);
        tile.title &&
          setClickHistory([
            ...clickHistory,
            {
              title: tile.title,
              type: 'entertainment',
            },
          ]);
      } else {
        updateFilterList?.(tile.type);

        tile.title &&
          !tileIsTenant &&
          setClickHistory([
            ...clickHistory,
            { title: tile.title, type: tile.type },
          ]);
      }
    }
    if (tile.floorPlanIDs?.some((id) => id !== null) ||
      tile.unitId?.some((id) => id !== null)) {
      await goToFloor();
      setActiveStore(() => undefined);
    }
    if (tile.floorPlanIDs ||
      tile?.unitId) {
      await clickOnTenant(tile);
    }
  };

  const dictionary = useGetLibraryConstantsDictionaryData();

  const floors: string[] = [];

  tile.floorPlanIDs?.forEach((id) => {
    const floor = checkFloorOfID(id);
    if (floor && !floors.includes(floor)) {
      floors.push(floor);
    }
  });

  tile.unitId?.forEach((id) => {
    const floor = checkFloorOfID(id);
    if (floor && !floors.includes(floor)) {
      floors.push(floor);
    }
  });

  const commonTileClasses = `mr-3 rounded-lg bg-brand-white cursor-pointer flex items-center lg:hover:bg-background-secondary md:active:bg-background-secondary ${isLastTile ? 'mb-4' : ' '
    }`;

  const levelIndexObj: { [key: string]: string | false | undefined } = {};
  activeBuilding?.floors.forEach((floor) => {
    const localizedShortName = getLocalizedShortName(floor.id);
    if (localizedShortName !== undefined) {
      levelIndexObj[floor.id] = localizedShortName;
    }
  });

  const borderClasses =
    'outline outline-3 outline-offset-3 outline-neutral-grey-400';

  return tileIsTenant ? (
    <button
      tabIndex={0}
      type="button"
      className={`${commonTileClasses} mt-2 w-full px-5 py-4 ${activeStoreMatch ? borderClasses : ''
        }`}
      onClick={handleOnClick}
    >
      <div className="mr-3 w-10">
        {tile.images?.logo ? (
          <Image
            alt={tile.images.logo.description || ''}
            src={tile.images.logo.url}
            width={
              tile.images?.logo?.width
                ? parseInt(tile.images?.logo?.width)
                : undefined
            }
            height={
              tile.images?.logo?.height
                ? parseInt(tile.images?.logo?.height)
                : undefined
            }
            className="h-10"
          />
        ) : (
          tile.images?.coverImage && (
            <ImageWrapper
              alt={
                tile.images.coverImage &&
                  'jsonBynderAsset' in tile.images.coverImage
                  ? ''
                  : tile.images.coverImage?.description
              }
              url={
                tile.images.coverImage &&
                  'jsonBynderAsset' in tile.images.coverImage
                  ? tile.images.coverImage?.jsonBynderAsset?.originalUrl
                  : tile.images.coverImage?.url
              }
              width={
                tile.images.coverImage &&
                  'jsonBynderAsset' in tile.images.coverImage
                  ? 80
                  : tile.images.coverImage?.width
                    ? parseInt(tile.images.coverImage?.width)
                    : 80
              }
              height={
                tile.images.coverImage &&
                  'jsonBynderAsset' in tile.images.coverImage
                  ? 80
                  : tile.images.coverImage?.height
                    ? parseInt(tile.images.coverImage?.height)
                    : 80
              }
              className="h-auto"
            />
          )
        )}
      </div>
      <div className="flex flex-col text-text-default">
        <div className="text-start font-title text-headings">{tile.title}</div>
        {floors.length > 0 && (
          <div className="text-n mt-1 flex items-center text-bodys">
            <LocationPin />
            <span className="ml-2 mr-1">{dictionary?.floor}</span>
            {floors
              .sort()
              .map((floor, index) => {
                if (floor === '' || floor === 'outside') {
                  return dictionary?.outside;
                } else {
                  const includesFloor =
                    Object.keys(levelIndexObj).includes(floor);
                  const level = includesFloor
                    ? levelIndexObj[floor]
                    : floor.slice(-1);
                  return `${index > 0 ? ', ' : ''}${level}`;
                }
              })
              .filter((val, index, self) => self.indexOf(val) === index)}
          </div>
        )}
      </div>
    </button>
  ) : (
    <button
      type="button"
      tabIndex={0}
      className={`${commonTileClasses} mt-3 inline-block min-h-[170px] w-[calc(50%-15px)] flex-col justify-center p-3 md:w-[calc(33%-15px)] lg:w-[calc(50%-15px)]`}
      onClick={handleOnClick}
    >
      {tile.images?.coverImage && (
        <ImageWrapper
          alt={
            tile.images.coverImage &&
              'jsonBynderAsset' in tile.images.coverImage
              ? ''
              : tile.images.coverImage?.description
          }
          url={
            tile.images.coverImage &&
              'jsonBynderAsset' in tile.images.coverImage
              ? tile.images.coverImage?.jsonBynderAsset?.originalUrl
              : tile.images.coverImage?.url
          }
          width={
            tile.images.coverImage &&
              'jsonBynderAsset' in tile.images.coverImage
              ? 80
              : tile.images.coverImage?.width
                ? parseInt(tile.images.coverImage?.width)
                : 80
          }
          height={
            tile.images.coverImage &&
              'jsonBynderAsset' in tile.images.coverImage
              ? 80
              : tile.images.coverImage?.height
                ? parseInt(tile.images.coverImage?.height)
                : 80
          }
          imgClassName="mb-4 min-w-[60px] max-w-[80px]"
        />
      )}
      <div className="px-3 text-center font-title text-headingxs text-neutral-grey-400">
        {tile.title}
      </div>
    </button>
  );
};
